var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { parse, stringify } from 'query-string';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import v4 from 'uuid';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { apiGetListConsultantSearchCount } from '../../../actions/SearchConsultantActions';
import { wrapDispatch } from '../../action';
import { sleep } from '../../common';
/**
 * 検索結果件数と検索履歴ID取得APIのuse
 * @param dispatch
 * @returns {{loadTotal: (function(*): *), loading}}
 */
export const useCallConsultantSearchCountApi = (dispatch) => {
    const loadTotal = useCallback((args) => __awaiter(void 0, void 0, void 0, function* () {
        const params = Object.assign({ searchFrom: window.location.pathname }, args);
        const { payload } = yield wrapDispatch(dispatch, apiGetListConsultantSearchCount(params));
        return payload;
    }), [dispatch]);
    return { loadTotal };
};
/**
 * コンサル検索ページに遷移するためのフォームのuse
 * @param callback
 * @param defaultKeyword
 * @param defaultProfessionalCount
 * @param defaultSolutionCount
 * @param includesSeminar
 * @param unmountRef
 * @returns {*}
 */
export const useConsultantSearchForm = (options) => {
    const { callback, defaultKeyword = '', defaultProfessionalCount = 0, defaultSeminarCount = 0, defaultSolutionCount = 0, includesSeminar = false, unmountRef, } = options;
    const { search: locationSearch, state: locationState = {} } = useLocation();
    const history = useNavigate();
    const query = useMemo(() => parse(locationSearch), [locationSearch]);
    const { search: querySearch } = query;
    // locationのstateから検索履歴を取得
    const { countData: { professional: locationProfessionalCount, seminar: locationSeminarCount, solution: locationSolutionCount, }, query: { keyword: stateKeyword, search: stateSearch }, } = useMemo(() => locationState && 'query' in locationState && 'countData' in locationState
        ? locationState
        : {
            countData: { professional: null, seminar: null, solution: null },
            query: {},
        }, [locationState]);
    // テーマ選択
    const optionColor = '#485C60';
    const searchOptions = useMemo(() => {
        const values = [
            {
                color: optionColor,
                id: 'professional',
                label: 'コンサルタント',
                value: 'search',
            },
            {
                color: optionColor,
                id: 'solution',
                label: '事例',
                value: 'service',
            },
        ];
        if (includesSeminar) {
            values.splice(1, 0, {
                color: optionColor,
                id: 'seminar',
                label: 'セミナー',
                value: 'seminar',
            });
        }
        return values;
    }, [includesSeminar]);
    // フォーム用の初期値設定
    const initialKeyword = useMemo(() => {
        if (stateKeyword) {
            return stateKeyword;
        }
        const value = defaultKeyword || query.keyword;
        return value || '';
    }, [stateKeyword, query, defaultKeyword]);
    const initialSearch = useMemo(() => {
        const value = stateSearch ? stateSearch.value : querySearch;
        return searchOptions.find((v) => v.value === value) || searchOptions[0];
    }, [searchOptions, stateSearch, querySearch]);
    const initialCountData = useMemo(() => ({
        professional: typeof locationProfessionalCount === 'number'
            ? locationProfessionalCount
            : defaultProfessionalCount,
        seminar: typeof locationSeminarCount === 'number'
            ? locationSeminarCount
            : defaultSeminarCount,
        solution: typeof locationSolutionCount === 'number'
            ? locationSolutionCount
            : defaultSolutionCount,
    }), [
        defaultProfessionalCount,
        defaultSolutionCount,
        defaultSeminarCount,
        locationProfessionalCount,
        locationSolutionCount,
        locationSeminarCount,
    ]);
    const keywordRef = useRef(null);
    const searchRef = useRef(null);
    const searchingRef = useRef(null);
    const [keyword, rawChangeKeyword] = useState(initialKeyword);
    const [search, rawChangeSearch] = useState(initialSearch);
    const [countData, rawChangeCountData] = useState(initialCountData);
    useEffect(() => {
        rawChangeKeyword(initialKeyword);
    }, [initialKeyword]);
    // 検索条件保持のため、historyのstateを更新
    const changeHistory = useCallback((args = {}) => {
        history.replace(window.location.pathname + window.location.search, {
            state: {
                countData: Object.assign(Object.assign({}, countData), args.countData),
                query: Object.assign({ keyword,
                    search }, args.query),
            },
        });
    }, [history, countData, keyword, search]);
    const changeSearch = useCallback((option) => {
        rawChangeSearch(option);
        changeHistory({
            query: {
                keyword,
                search: option,
            },
        });
    }, [changeHistory, keyword, rawChangeSearch]);
    // キーワード変更時に採番した履歴IDを削除する
    const changeKeyword = useCallback((value) => {
        rawChangeKeyword(value);
        rawChangeCountData((prevState) => (Object.assign(Object.assign({}, prevState), { baseId: null })));
    }, [rawChangeCountData, rawChangeKeyword]);
    // 件数取得コールバックを実行し、レスポンスを保存
    const changeCountData = useCallback((params) => __awaiter(void 0, void 0, void 0, function* () {
        // すでに検索実行中であれば履歴保存しない
        const apiId = searchingRef.current;
        yield sleep(0.5);
        if (apiId !== searchingRef.current) {
            return;
        }
        const res = yield callback(params);
        if (unmountRef.current) {
            return;
        }
        rawChangeCountData(res);
        changeHistory({
            countData: res,
            query: {
                keyword: params.keyword,
                search,
            },
        });
    }), [
        callback,
        changeHistory,
        rawChangeCountData,
        search,
        searchingRef,
        unmountRef,
    ]);
    // 件数データをデフォルトに戻す
    const resetCountData = useCallback(() => {
        rawChangeCountData({
            baseId: null,
            professional: defaultProfessionalCount,
            seminar: defaultSeminarCount,
            solution: defaultSolutionCount,
        });
    }, [
        defaultProfessionalCount,
        defaultSolutionCount,
        defaultSeminarCount,
        rawChangeCountData,
    ]);
    // 検索ページにクエリ付きで移動
    const onSearch = useCallback((args = {}) => __awaiter(void 0, void 0, void 0, function* () {
        searchingRef.current = v4();
        let params = Object.assign({ keyword, search: search.value, searchFrom: window.location.pathname }, args);
        const { baseId } = countData;
        if (baseId) {
            params = Object.assign(Object.assign({}, params), { baseId });
        }
        // 検索ページに遷移
        if (!unmountRef.current) {
            history.push(`/consultant/search?${stringify(params)}`);
        }
    }), [history, countData, keyword, unmountRef, search, searchingRef]);
    useEffect(() => {
        const currentSearchOption = searchOptions.find((v) => v.value === querySearch);
        if (currentSearchOption) {
            rawChangeSearch(currentSearchOption);
        }
    }, [querySearch, searchOptions, rawChangeSearch]);
    return {
        changeCountData,
        changeKeyword,
        changeSearch,
        countData,
        initialKeyword,
        keyword,
        keywordRef,
        onSearch,
        resetCountData,
        search,
        searchOptions,
        searchRef,
    };
};
export const useConsultantSearchCount = {
    useCallConsultantSearchCountApi,
    useConsultantSearchForm,
};
